import { useState } from "react";
import { Star, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/FormBits";
import { isFeatured } from "@/lib/hh";

export default function Featured() {
  const [email, setEmail] = useState("");
  const [listings, setListings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get("/featured/listings", { params: { email } });
      setListings(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const request = async (venue_id) => {
    try {
      const { data } = await api.post("/featured/request", { venue_id, email });
      setOrder(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="page max-w-3xl">
      <p className="eyebrow">For approved owners</p>
      <h1 className="font-heading mt-2 text-5xl text-white sm:text-6xl">Get Featured</h1>
      <div className="panel mt-6 flex items-center gap-4 p-6">
        <Star className="shrink-0 text-amber-400" fill="currentColor" size={30} />
        <p className="text-zinc-300"><span className="text-2xl font-extrabold text-[#fde68a]">$19.99</span> for 7 days at the top of results with a gold <span className="font-bold text-white">Featured</span> badge and a gold map pin.</p>
      </div>
      {order ? (
        <div data-testid="featured-order-pending" className="panel mt-6 p-6">
          <Clock className="mb-3 text-[#fde68a]" />
          <h2 className="font-heading text-3xl text-white">Request received for {order.venue_name}</h2>
          <p className="mt-2 text-zinc-300">Online card payment is coming soon. We'll contact you at {order.email} to complete the $19.99 payment. Your 7 days start as soon as payment is confirmed.</p>
        </div>
      ) : (
        <>
          <form onSubmit={lookup} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1"><Field label="Email you used when submitting your bar" id="featured-email-input" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setListings(null); }} /></div>
            <button data-testid="featured-lookup-button" disabled={busy} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-[#fde68a] px-6 font-bold text-zinc-950">{busy && <Loader2 size={16} className="animate-spin" />} Find my listings</button>
          </form>
          {listings && !listings.length && <p data-testid="featured-no-listings" className="panel mt-6 p-6 text-zinc-300">No approved listings for this email yet. Only approved listings can be Featured.</p>}
          <div className="mt-6 space-y-3">
            {(listings || []).map((l) => (
              <div key={l.id} data-testid={`featured-listing-${l.id}`} className="panel flex flex-wrap items-center justify-between gap-3 p-5">
                <div>
                  <p className="font-semibold text-white">{l.name}</p>
                  <p className="text-sm text-zinc-500">{l.address}</p>
                  {isFeatured(l) && <p className="mt-1 text-xs font-bold text-amber-400">Featured until {new Date(l.featured_until).toLocaleDateString()}</p>}
                </div>
                <button data-testid={`featured-request-${l.id}`} onClick={() => request(l.id)} className="rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-zinc-950">{isFeatured(l) ? "Add 7 more days · $19.99" : "Feature for $19.99"}</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
